import { Schema } from 'effect';

import type { CanonicalBlock, CanonicalParseResult } from '../types';

export interface ParserCitationAnchorProjection {
  readonly id: string;
  readonly pageNumber: number;
  readonly bbox: { readonly x: number; readonly y: number; readonly w: number; readonly h: number };
  readonly text?: string;
  readonly label?: string;
  readonly type?: string;
  readonly provider: string;
  readonly providerBlockId?: string;
  readonly payloadPath?: string;
}

export const ParserCitationAnchorProjectionSchema = Schema.Struct({
  id: Schema.String,
  pageNumber: Schema.Int,
  bbox: Schema.Struct({
    x: Schema.Finite,
    y: Schema.Finite,
    w: Schema.Finite,
    h: Schema.Finite,
  }),
  text: Schema.optionalKey(Schema.String),
  label: Schema.optionalKey(Schema.String),
  type: Schema.optionalKey(Schema.String),
  provider: Schema.String,
  providerBlockId: Schema.optionalKey(Schema.String),
  payloadPath: Schema.optionalKey(Schema.String),
});

function collectBlockAnchors(
  block: CanonicalBlock,
  provider: string,
  pageNumber: number,
  path: string,
): ParserCitationAnchorProjection[] {
  const anchor = block.bbox
    ? [
        {
          id: `${provider}:${pageNumber}:${path}`,
          pageNumber,
          bbox: { ...block.bbox },
          ...(block.value === undefined ? {} : { text: block.value }),
          ...(block.label === undefined ? {} : { label: block.label }),
          type: block.type,
          provider,
          providerBlockId: path,
          payloadPath: path,
        },
      ]
    : [];
  const children = (block.children ?? []).flatMap((child, index) =>
    collectBlockAnchors(child, provider, pageNumber, `${path}.children.${index}`),
  );
  return [...anchor, ...children];
}

/** Structural projection: parser-sdk intentionally does not depend on plugin-sdk. */
export function toCitationAnchors(
  result: CanonicalParseResult,
  provider: string,
): ParserCitationAnchorProjection[] {
  return result.pages.flatMap((page, pageIndex) =>
    page.blocks.flatMap((block, blockIndex) =>
      collectBlockAnchors(
        block,
        provider,
        page.pageNumber,
        `pages.${pageIndex}.blocks.${blockIndex}`,
      ),
    ),
  );
}
